import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useServers } from '../hooks/use-servers.js';
import { useAllSchedules, useAllScheduleRuns, type ServerSchedules, type ServerScheduleRuns } from '../hooks/use-schedules.js';
import type { Schedule, ScheduleRun } from '../api/types.js';
import { timeAgo } from '../utils/time-ago.js';

function parseCron(expression: string): string {
  const match = expression.match(/^cron\((.+)\)$/);
  return match ? match[1] : expression;
}

function cronToHuman(cron: string): string {
  const parts = parseCron(cron).split(/\s+/);
  if (parts.length < 5) return cron;
  const [min, hour, , , dayOfWeek] = parts;
  const h = hour === '*' ? '' : `${hour.padStart(2, '0')}:${(min ?? '0').padStart(2, '0')} UTC`;
  const dow = dayOfWeek === '?' || dayOfWeek === '*' ? 'daily' : `day ${dayOfWeek}`;
  return [dow, h].filter(Boolean).join(' @ ');
}

function formatTimeout(seconds: number): string {
  if (seconds >= 3600) return `${(seconds / 3600).toFixed(1)}h`;
  if (seconds >= 60) return `${Math.round(seconds / 60)}m`;
  return `${seconds}s`;
}


function formatDuration(startedAt: string, lastActivityAt: string): string {
  const ms = new Date(lastActivityAt).getTime() - new Date(startedAt).getTime();
  if (ms < 0) return '0s';
  const totalSec = Math.floor(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min < 60) return sec > 0 ? `${min}m ${sec}s` : `${min}m`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return remMin > 0 ? `${hr}h ${remMin}m` : `${hr}h`;
}

/* ── Skeleton ─────────────────────────────────────────────────────────── */

function ServerScheduleSkeleton({ delay }: { delay: number }) {
  return (
    <div
      className="card overflow-hidden"
      style={{ animationDelay: `${delay}ms`, animation: 'schedule-fade-in 0.3s ease-out both' }}
    >
      <div
        className="flex items-center gap-3 px-5 py-3 animate-pulse"
        style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface-2)' }}
      >
        <div className="h-3.5 w-40 rounded" style={{ backgroundColor: 'var(--color-surface-3)' }} />
        <div className="h-3 w-20 rounded" style={{ backgroundColor: 'var(--color-surface-3)' }} />
      </div>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="animate-pulse flex items-center gap-4 px-5 py-3.5"
          style={{ borderBottom: i < 2 ? '1px solid var(--color-border)' : undefined }}
        >
          <div className="h-3 w-2 rounded-sm" style={{ backgroundColor: 'var(--color-surface-3)' }} />
          <div className="h-3 w-36 rounded" style={{ backgroundColor: 'var(--color-surface-3)' }} />
          <div className="h-3 w-32 rounded" style={{ backgroundColor: 'var(--color-surface-3)' }} />
          <div className="h-3 w-14 rounded" style={{ backgroundColor: 'var(--color-surface-3)' }} />
          <div className="h-3 w-28 rounded" style={{ backgroundColor: 'var(--color-surface-3)' }} />
          <div className="h-3 w-10 rounded" style={{ backgroundColor: 'var(--color-surface-3)' }} />
          <div className="ml-auto h-4 w-16 rounded" style={{ backgroundColor: 'var(--color-surface-3)' }} />
        </div>
      ))}
    </div>
  );
}

/* ── Run History Row ─────────────────────────────────────────────────── */

function RunRow({ run, serverId }: { run: ScheduleRun; serverId: string }) {
  const isRunning = run.status === 'running';

  return (
    <tr
      style={{
        borderBottom: '1px solid var(--color-border)',
        backgroundColor: 'var(--color-surface)',
      }}
    >
      {/* Started */}
      <td
        style={{
          padding: '8px 16px 8px 28px',
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          color: 'var(--color-text-2)',
        }}
      >
        {timeAgo(run.startedAt)}
      </td>

      {/* Duration */}
      <td
        style={{
          padding: '8px 16px',
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          color: 'var(--color-text-2)',
        }}
      >
        {formatDuration(run.startedAt, run.lastActivityAt)}
      </td>

      {/* Status */}
      <td style={{ padding: '8px 16px' }}>
        <span className={`badge ${isRunning ? 'badge-running' : 'badge-stopped'}`}>
          <span className="badge-dot" />
          {run.status}
        </span>
      </td>

      {/* Model */}
      <td style={{ padding: '8px 16px' }}>
        {run.model ? (
          <span className={`badge-model badge-model-${run.model}`} style={{ textTransform: 'uppercase' }}>
            {run.model}
          </span>
        ) : (
          <span style={{ color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>&mdash;</span>
        )}
      </td>

      {/* Session link */}
      <td style={{ padding: '8px 16px' }}>
        <Link
          to={`/servers/${serverId}/sessions/${run.sessionId}`}
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: 'var(--color-brand)',
            textDecoration: 'none',
            transition: 'opacity 0.15s',
          }}
          className="schedule-server-link"
        >
          {run.sessionName || run.sessionId.slice(0, 8)}
        </Link>
      </td>
    </tr>
  );
}

/* ── Run History Panel ───────────────────────────────────────────────── */

const RUN_HEADERS = ['started', 'duration', 'status', 'model', 'session'];

function RunHistoryPanel({ runs, serverId }: { runs: ScheduleRun[]; serverId: string }) {
  if (runs.length === 0) {
    return (
      <tr>
        <td colSpan={8}>
          <div
            style={{
              borderLeft: '2px solid var(--color-brand)',
              borderLeftStyle: 'solid',
              opacity: 0.3,
              marginLeft: 12,
              padding: '12px 16px 12px 14px',
              backgroundColor: 'var(--color-surface)',
            }}
          >
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-text-3)', letterSpacing: '0.02em' }}>
              No executions recorded
            </p>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td colSpan={8} style={{ padding: 0 }}>
        <div
          style={{
            borderLeft: '2px solid var(--color-brand)',
            opacity: 1,
            marginLeft: 12,
            backgroundColor: 'var(--color-surface)',
            animation: 'schedule-runs-expand 0.2s ease-out both',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                {RUN_HEADERS.map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: h === 'started' ? '6px 16px 6px 28px' : '6px 16px',
                      textAlign: 'left',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 9,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase' as const,
                      color: 'var(--color-text-3)',
                      fontWeight: 500,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <RunRow key={run.sessionId} run={run} serverId={serverId} />
              ))}
            </tbody>
          </table>
        </div>
      </td>
    </tr>
  );
}

/* ── Schedule Row with Expand ────────────────────────────────────────── */

function ScheduleRowWithRuns({
  schedule,
  index,
  runs,
  serverId,
}: {
  schedule: Schedule;
  index: number;
  runs: ScheduleRun[];
  serverId: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const enabled = schedule.state === 'ENABLED';

  return (
    <>
      <tr className="schedule-row" style={{ animationDelay: `${index * 40}ms` }}>
        {/* Status rail */}
        <td style={{ width: 3, padding: 0 }}>
          <div
            style={{
              width: 3,
              height: '100%',
              minHeight: 44,
              backgroundColor: enabled ? 'var(--color-brand)' : 'var(--color-border)',
              opacity: enabled ? 0.7 : 0.4,
              transition: 'background-color 0.2s',
            }}
          />
        </td>

        {/* Name */}
        <td
          style={{
            padding: '11px 16px 11px 12px',
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
            color: 'var(--color-text)',
            fontWeight: 500,
            letterSpacing: '0.01em',
          }}
        >
          {schedule.name}
        </td>

        {/* Cron expression + human hint */}
        <td style={{ padding: '11px 16px' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-2)', letterSpacing: '0.02em' }}>
            {parseCron(schedule.cron)}
          </div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 10, color: 'var(--color-text-3)', marginTop: 2 }}>
            {cronToHuman(schedule.cron)}
          </div>
        </td>

        {/* Model */}
        <td style={{ padding: '11px 16px' }}>
          {schedule.model ? (
            <span className={`badge-model badge-model-${schedule.model}`} style={{ textTransform: 'uppercase' }}>
              {schedule.model}
            </span>
          ) : (
            <span style={{ color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>&mdash;</span>
          )}
        </td>

        {/* Session name */}
        <td
          style={{
            padding: '11px 16px',
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            color: schedule.sessionName ? 'var(--color-text-2)' : 'var(--color-text-3)',
          }}
        >
          {schedule.sessionName || '\u2014'}
        </td>

        {/* Timeout */}
        <td
          style={{
            padding: '11px 16px',
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            color: schedule.timeout ? 'var(--color-text-2)' : 'var(--color-text-3)',
          }}
        >
          {schedule.timeout ? formatTimeout(schedule.timeout) : '\u2014'}
        </td>

        {/* State badge */}
        <td style={{ padding: '11px 16px' }}>
          <span className={`badge ${enabled ? 'badge-running' : 'badge-stopped'}`}>
            <span className="badge-dot" />
            {schedule.state.toLowerCase()}
          </span>
        </td>

        {/* Expand button */}
        <td style={{ padding: '11px 12px 11px 4px', width: 32 }}>
          <button
            onClick={() => setExpanded(!expanded)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              backgroundColor: 'transparent',
              color: 'var(--color-text-3)',
              cursor: 'pointer',
              transition: 'color 0.15s, background-color 0.15s',
            }}
            className="schedule-expand-btn"
            title={expanded ? 'Collapse runs' : 'Expand runs'}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transition: 'transform 0.2s ease',
                transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </td>
      </tr>

      {expanded && <RunHistoryPanel runs={runs} serverId={serverId} />}
    </>
  );
}

/* ── Server Schedule Card ────────────────────────────────────────────── */

const COLUMN_HEADERS = ['name', 'cron', 'model', 'session', 'timeout', 'state', ''];

function ServerScheduleCard({
  scheduleData,
  runsData,
  index,
}: {
  scheduleData: ServerSchedules;
  runsData: ServerScheduleRuns | undefined;
  index: number;
}) {
  const { server, schedules, isLoading, error } = scheduleData;
  const enabledCount = schedules.filter((s) => s.state === 'ENABLED').length;
  const runs = runsData?.runs ?? {};

  if (isLoading) {
    return <ServerScheduleSkeleton delay={index * 100} />;
  }

  return (
    <div
      className="card overflow-hidden"
      style={{ animation: `schedule-fade-in 0.35s ease-out ${index * 80}ms both` }}
    >
      {/* Server header */}
      <div
        className="flex items-center justify-between px-5 py-3"
        style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface-2)' }}
      >
        <div className="flex items-center gap-3">
          <Link
            to={`/servers/${server.id}`}
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--color-text)',
              letterSpacing: '0.01em',
              textDecoration: 'none',
              transition: 'color 0.15s',
            }}
            className="schedule-server-link"
          >
            {server.name || server.id}
          </Link>
        </div>
        <div className="flex items-center gap-3">
          {schedules.length > 0 && (
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                letterSpacing: '0.06em',
                padding: '2px 7px',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: 'var(--color-brand-dim)',
                color: 'var(--color-brand)',
                fontWeight: 500,
              }}
            >
              {enabledCount}/{schedules.length}
            </span>
          )}
          <span className={`badge badge-${server.status === 'running' ? 'running' : server.status === 'stopped' ? 'stopped' : 'warn'}`}>
            <span className="badge-dot" />
            {server.status}
          </span>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div
          className="px-5 py-3 text-sm"
          style={{ color: 'var(--color-error)', backgroundColor: 'var(--color-error-dim)' }}
        >
          Failed to load: {error.message}
        </div>
      )}

      {/* Empty state */}
      {!error && schedules.length === 0 && (
        <div className="px-5 py-6 text-center">
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-3)', letterSpacing: '0.02em' }}>
            No schedules configured
          </p>
        </div>
      )}

      {/* Schedule table */}
      {schedules.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <th style={{ width: 3, padding: 0 }} />
              {COLUMN_HEADERS.map((h, i) => (
                <th
                  key={h || `expand-${i}`}
                  style={{
                    padding: h === 'name' ? '8px 16px 8px 12px' : '8px 16px',
                    textAlign: 'left',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 9,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase' as const,
                    color: 'var(--color-text-3)',
                    fontWeight: 500,
                    width: h === '' ? 32 : undefined,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {schedules.map((schedule, idx) => (
              <ScheduleRowWithRuns
                key={schedule.name}
                schedule={schedule}
                index={idx}
                runs={runs[schedule.name] ?? []}
                serverId={server.id}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ── Organization Section ────────────────────────────────────────────── */

interface OrgGroup {
  organization: string;
  servers: { scheduleData: ServerSchedules; runsData: ServerScheduleRuns | undefined }[];
}

function OrgSection({ group, orgIndex }: { group: OrgGroup; orgIndex: number }) {
  const [collapsed, setCollapsed] = useState(false);

  const totalServers = group.servers.length;
  const totalSchedules = group.servers.reduce((sum, s) => sum + s.scheduleData.schedules.length, 0);
  const enabledSchedules = group.servers.reduce(
    (sum, s) => sum + s.scheduleData.schedules.filter((sc) => sc.state === 'ENABLED').length, 0
  );

  return (
    <div style={{ animation: `schedule-fade-in 0.35s ease-out ${orgIndex * 120}ms both` }}>
      {/* Org header */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          width: '100%',
          padding: '8px 4px',
          marginBottom: collapsed ? 0 : 12,
          border: 'none',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          transition: 'margin-bottom 0.2s',
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            color: 'var(--color-text-3)',
            transition: 'transform 0.2s ease',
            transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
            flexShrink: 0,
          }}
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase' as const,
            color: 'var(--color-text-2)',
          }}
        >
          {group.organization}
        </span>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '0.04em',
            color: 'var(--color-text-3)',
          }}
        >
          {totalServers} {totalServers === 1 ? 'server' : 'servers'}
        </span>
        {totalSchedules > 0 && (
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              letterSpacing: '0.06em',
              padding: '1px 6px',
              borderRadius: 'var(--radius-sm)',
              backgroundColor: 'var(--color-brand-dim)',
              color: 'var(--color-brand)',
              fontWeight: 500,
            }}
          >
            {enabledSchedules}/{totalSchedules}
          </span>
        )}
      </button>

      {/* Server cards */}
      {!collapsed && (
        <div className="space-y-3" style={{ paddingLeft: 24 }}>
          {group.servers.map(({ scheduleData, runsData }, idx) => (
            <ServerScheduleCard
              key={scheduleData.server.id}
              scheduleData={scheduleData}
              runsData={runsData}
              index={idx}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Main Page ────────────────────────────────────────────────────────── */

export function Schedules() {
  const { data: servers, isLoading: serversLoading, error: serversError } = useServers();
  const { data: allSchedules, isLoading: schedulesLoading } = useAllSchedules(servers);
  const { data: allRuns, isLoading: runsLoading } = useAllScheduleRuns(servers);

  const totalEnabled = allSchedules.reduce(
    (sum, s) => sum + s.schedules.filter((sc) => sc.state === 'ENABLED').length, 0
  );
  const totalSchedules = allSchedules.reduce((sum, s) => sum + s.schedules.length, 0);

  const isLoading = serversLoading || schedulesLoading || runsLoading;

  // Group by organization
  const orgGroups: OrgGroup[] = [];
  const orgMap = new Map<string, OrgGroup>();

  for (const scheduleData of allSchedules) {
    const org = scheduleData.server.organization;
    let group = orgMap.get(org);
    if (!group) {
      group = { organization: org, servers: [] };
      orgMap.set(org, group);
      orgGroups.push(group);
    }
    const runsData = allRuns.find((r) => r.server.id === scheduleData.server.id);
    group.servers.push({ scheduleData, runsData });
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <h1 className="page-title text-2xl">Schedules</h1>
          {!isLoading && totalSchedules > 0 && (
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                letterSpacing: '0.06em',
                padding: '2px 8px',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: 'var(--color-brand-dim)',
                color: 'var(--color-brand)',
                fontWeight: 500,
              }}
            >
              {totalEnabled}/{totalSchedules}
            </span>
          )}
        </div>
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-2)' }}>
          Automated runs via EventBridge Scheduler, grouped by organization.
        </p>
      </div>

      {/* Error loading servers */}
      {serversError && (
        <div
          className="rounded-lg border p-4 text-sm"
          style={{
            borderColor: 'var(--color-error)',
            backgroundColor: 'var(--color-error-dim)',
            color: 'var(--color-error)',
          }}
        >
          Failed to load servers: {serversError.message}
        </div>
      )}

      {/* Loading */}
      {serversLoading && (
        <div className="space-y-4">
          <ServerScheduleSkeleton delay={0} />
          <ServerScheduleSkeleton delay={100} />
        </div>
      )}

      {/* Empty -- no servers */}
      {servers && servers.length === 0 && (
        <div
          className="rounded-xl border-2 border-dashed p-12 text-center"
          style={{ borderColor: 'var(--color-border-2)' }}
        >
          <svg
            className="mx-auto h-12 w-12"
            style={{ color: 'var(--color-text-3)' }}
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1}
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
          </svg>
          <h3 className="mt-3 text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
            No servers
          </h3>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-text-3)' }}>
            No servers are configured for your organization.
          </p>
        </div>
      )}

      {/* Organization sections */}
      {orgGroups.length > 0 && (
        <div className="space-y-6">
          {orgGroups.map((group, idx) => (
            <OrgSection key={group.organization} group={group} orgIndex={idx} />
          ))}
        </div>
      )}
    </div>
  );
}
